import React from 'react';
import { Calendar, CalendarDays, CalendarRange, CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import SelectOption from '../ui/SelectOption';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

const EMPLOYMENT_FREQUENCIES = [
  { value: 'weekly', icon: Calendar },
  { value: 'biweekly', icon: CalendarDays },
  { value: 'twice_monthly', icon: CalendarRange },
  { value: 'monthly', icon: CalendarClock },
] as const;

type EmploymentFrequencyFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function EmploymentFrequencyForm({ formData, onSubmit }: EmploymentFrequencyFormProps) {
  const { t } = useTranslation('wizard');
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  return (
    <div id="employment-frequency-form" className="space-y-6">
      <div className="text-center">
        <Title 
          id="employment-frequency-title"
          name="employment-frequency-title"
          as="h1" 
          className="mb-2"
        >
          {t('employmentFrequency.title')}
        </Title>
        <Description 
          id="employment-frequency-description"
          name="employment-frequency-description"
          size="lg"
        >
          {t('employmentFrequency.description')}
        </Description>
      </div>

      <div className="grid gap-3">
        {EMPLOYMENT_FREQUENCIES.map(({ value, icon }) => (
          <SelectOption
            key={value}
            id={`employment-frequency-option-${value}`}
            name={`employment-frequency-option-${value}`}
            icon={icon}
            label={t(`employmentFrequency.options.${value}.label`)}
            description={t(`employmentFrequency.options.${value}.description`)}
            onClick={() => onSubmit({ employmentFrequency: value })}
          />
        ))}
      </div>

      {showDisclaimer && (
        <Disclaimer
          id="employment-frequency-disclaimer"
          name="employment-frequency-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}
    </div>
  );
}